/**
 * JourneyLibraryPage — /journeys/:slug  (Page 3 of master mockup)
 *
 * Layout (parchment-primary):
 *  1. Top nav (BACK · other journeys)
 *  2. Hero — Title (English) + Devanagari + italic tagline + description
 *           with a soft painterly illustration zone above (mountain/sunrise).
 *  3. "Featured Books in {Journey}" — 5 vintage book artifact cards
 *  4. Rust CTA "View All 35+ Books in This Journey"
 *  5. 4 vintage icon tiles (Curated Books / Proven Ideas / Action Plans / Real Change)
 *  6. Closing ribbon
 */
import React from "react";
import { bookIdForSlug } from "../data/mycraftzShowcase";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "motion/react";
import {
  ArrowLeft, ArrowRight, BookOpen, Lightbulb, ClipboardList, Sparkles,
} from "lucide-react";
import { JOURNEYS, journeyByKey } from "../data/journeys";
import { GlobalChromeRibbon } from "../components/GlobalChrome";
import "../styles/Parchment.css";
import "../styles/JourneyLibraryPage.css";

const SHOWCASE_SLUGS = {
  "Atomic Habits": "atomic-habits",
  "Deep Work": "deep-work",
  "The Psychology of Money": "psychology-of-money",
  "Think and Grow Rich": "think-and-grow-rich",
  "Man's Search for Meaning": "mans-search-for-meaning",
};

const VALUE_TILES = [
  { icon: BookOpen,      label: "CURATED BOOKS",  sub: "Hand-picked for this path." },
  { icon: Lightbulb,     label: "PROVEN IDEAS",   sub: "Time-tested wisdom that works." },
  { icon: ClipboardList, label: "ACTION PLANS",   sub: "Daily blueprints, not theory." },
  { icon: Sparkles,      label: "REAL CHANGE",    sub: "Identity over information." },
];

/* Lightweight painterly hero illustration drawn as gradient layers.
   Used as a parchment-friendly stand-in until Phase E Nano Banana art lands. */
function PainterlyHero({ journeyKey }) {
  // Variants per journey
  const palettes = {
    aarohan: { sky: "#F1D9A4", mid: "#C99A56", far: "#8A5A2B", near: "#5C3818" },
    nirmaan: { sky: "#EBD7A4", mid: "#B79154", far: "#79522B", near: "#4F3318" },
    "nava-aarambh": { sky: "#F4DBA9", mid: "#D3A058", far: "#946530", near: "#5F3F1E" },
    tattva:  { sky: "#EFD49A", mid: "#B88E48", far: "#7A5328", near: "#4C3217" },
  };
  const p = palettes[journeyKey] || palettes.aarohan;
  return (
    <svg
      viewBox="0 0 1200 480"
      className="jl-painterly"
      role="img"
      aria-label={`Painterly hero for ${journeyKey} journey`}
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={p.sky} stopOpacity="1" />
          <stop offset="100%" stopColor={p.mid} stopOpacity="0.85" />
        </linearGradient>
        <radialGradient id="sun" cx="50%" cy="40%" r="40%">
          <stop offset="0%" stopColor="#F9E7B4" stopOpacity="0.95" />
          <stop offset="60%" stopColor="#F9E7B4" stopOpacity="0.0" />
        </radialGradient>
        <linearGradient id="vignette" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={p.sky} stopOpacity="0" />
          <stop offset="100%" stopColor="#1A0F0A" stopOpacity="0.55" />
        </linearGradient>
      </defs>

      {/* Sky */}
      <rect x="0" y="0" width="1200" height="480" fill="url(#sky)" />
      {/* Sun glow */}
      <circle cx="600" cy="190" r="320" fill="url(#sun)" />

      {/* Far range */}
      <path d="M 0 340 L 200 240 L 360 320 L 560 220 L 760 320 L 960 240 L 1200 320 L 1200 480 L 0 480 Z"
            fill={p.far} opacity="0.85" />
      {/* Mid range */}
      <path d="M 0 380 L 180 310 L 340 360 L 540 290 L 760 360 L 960 310 L 1200 360 L 1200 480 L 0 480 Z"
            fill={p.mid} opacity="0.9" />
      {/* Near range */}
      <path d="M 0 430 L 220 380 L 420 420 L 640 360 L 880 410 L 1080 380 L 1200 410 L 1200 480 L 0 480 Z"
            fill={p.near} />
      {/* Tiny silhouette figure on near range */}
      <g transform="translate(610 372)">
        <ellipse cx="0" cy="10" rx="8" ry="3" fill="#2C1A12" opacity="0.5" />
        <path d="M -2 8 L -2 -4 L 0 -8 L 2 -4 L 2 8 Z" fill="#2C1A12" />
        <circle cx="0" cy="-12" r="3" fill="#2C1A12" />
      </g>
      {/* Bottom vignette to merge with parchment */}
      <rect x="0" y="240" width="1200" height="240" fill="url(#vignette)" />

      {/* Top brass arch flourish */}
      <path d="M 0 8 Q 600 30 1200 8" stroke="#8B6508" strokeWidth="1.5" fill="none" opacity="0.5" />
    </svg>
  );
}

export default function JourneyLibraryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const known = JOURNEYS.find((j) => j.key === slug);
  const journey = known || journeyByKey("aarohan");

  return (
    <main className="parchment-theme jl-page" data-testid="journey-library-page">
      {/* Top nav */}
      <div className="jl-nav">
        <button
          type="button"
          className="pt-btn-walnut"
          onClick={() => navigate("/")}
          data-testid="jl-back-home"
        >
          <ArrowLeft size={14} /> Back to iCraftz
        </button>
        <div className="jl-other-journeys" aria-label="Other journeys">
          {JOURNEYS.filter((j) => j.key !== journey.key).map((j) => (
            <Link
              key={j.key}
              to={`/journeys/${j.key}`}
              className="pt-link jl-other-link"
              data-testid={`jl-other-${j.key}`}
            >
              {j.title} <ArrowRight size={11} style={{ verticalAlign: "middle" }} />
            </Link>
          ))}
        </div>
      </div>

      {/* Hero — painterly Nano Banana illustration + parchment title block */}
      <header className="jl-hero" data-testid={`jl-hero-${journey.key}`}>
        <div className="jl-hero-art" aria-hidden="true">
          <img
            src={`${process.env.PUBLIC_URL || ""}/illustrations/${journey.key}.png`}
            alt=""
            loading="eager"
            className="jl-hero-img"
          />
        </div>
        <motion.div
          className="jl-hero-inner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="pt-eyebrow">A CURATED READING JOURNEY</span>
          <h1 className="jl-title">{journey.title.toUpperCase()}</h1>
          <p className="jl-hindi indic">{journey.hindi}</p>
          <p className="pt-tagline jl-tagline">{journey.tagline}</p>
          <p className="pt-body jl-description">{journey.description}</p>
          <div className="pt-divider"><span /><em>❦</em><span /></div>
        </motion.div>
      </header>

      {/* Featured Books — vintage artifact cards */}
      <section className="jl-collection" aria-label={`${journey.title} curated collection`}>
        <header className="jl-collection-head">
          <span className="pt-eyebrow">FEATURED BOOKS IN {journey.title.toUpperCase()}</span>
          <h2 className="jl-collection-title">The Curated Collection</h2>
          <p className="pt-tagline">
            Five hand-picked beginnings. Each a doorway into the {journey.title} path.
          </p>
        </header>

        <ul className="jl-grid" data-testid="jl-book-grid">
          {journey.books.map((b, i) => {
            const showcaseSlug = SHOWCASE_SLUGS[b.title];
            const cover = `https://covers.openlibrary.org/b/isbn/${b.isbn}-L.jpg`;
            return (
              <motion.li
                key={b.title}
                className="jl-artifact"
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`jl-book-${b.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              >
                <div className="jl-artifact-frame">
                  <div className="jl-artifact-cover-wrap">
                    <img
                      src={cover}
                      alt={`${b.title} cover`}
                      loading="lazy"
                      onError={(e) => { e.target.style.display = "none"; }}
                      className="jl-artifact-cover"
                    />
                    <div className="jl-artifact-cover-fallback" aria-hidden="true">
                      <span className="jl-fb-title">{b.title}</span>
                      <em className="jl-fb-author">{b.author}</em>
                    </div>
                  </div>
                  <div className="jl-artifact-plate">
                    <p className="jl-artifact-title">{b.title}</p>
                    <p className="jl-artifact-author">{b.author}</p>
                    {showcaseSlug ? (
                      <Link
                        to={`/bodha?book=${bookIdForSlug(showcaseSlug) ?? showcaseSlug}&showcase=${showcaseSlug}`}
                        className="pt-link jl-artifact-cta"
                        data-testid={`jl-mycraftz-${showcaseSlug}`}
                      >
                        Install into MyCraftz<sup>™</sup> <ArrowRight size={11} style={{ verticalAlign: "middle" }} />
                      </Link>
                    ) : (
                      <span className="jl-artifact-soft">
                        <BookOpen size={12} /> Added soon
                      </span>
                    )}
                  </div>
                </div>
              </motion.li>
            );
          })}
        </ul>

        <div className="jl-collection-cta">
          <button
            type="button"
            className="pt-btn-rust"
            onClick={() => navigate("/#library")}
            data-testid="jl-view-all"
          >
            View all {journey.bookCount.replace(/\s.*/, "")} books in this journey <ArrowRight size={14} />
          </button>
          <p className="jl-more-note" data-testid="jl-more-note">
            Additional titles in this journey will be added over time.
          </p>
        </div>
      </section>

      {/* 4 vintage icon-tiles */}
      <section className="jl-values" aria-label="What this journey offers" data-testid="jl-values">
        {VALUE_TILES.map(({ icon: Icon, label, sub }, i) => (
          <motion.div
            key={label}
            className="pt-card pt-icon-tile jl-value"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`jl-value-${i}`}
          >
            <span className="pt-icon-wrap"><Icon size={22} /></span>
            <span className="pt-icon-label">{label}</span>
            <span className="pt-icon-sub">{sub}</span>
          </motion.div>
        ))}
      </section>

      {/* Back-to-home strip */}
      <div className="jl-foot">
        <button
          type="button"
          className="pt-btn-walnut"
          onClick={() => navigate("/")}
          data-testid="jl-return-home"
        >
          <ArrowLeft size={14} /> Return to iCraftz
        </button>
      </div>

      <GlobalChromeRibbon />
    </main>
  );
}
